import React, { useEffect, useMemo, useRef, useState } from "react";
import { composeWidgetDocument } from "../../widgets/composeWidgetDocument";

const READY_TIMEOUT_MS = 5000;
const MIN_HEIGHT = 200;
const MAX_HEIGHT = 800;

export function WidgetHost({ html, title, streaming }) {
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(MIN_HEIGHT);
  const [ready, setReady] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const { srcdoc, srcdocError } = useMemo(() => {
    if (streaming) return { srcdoc: null, srcdocError: null };
    try {
      return { srcdoc: composeWidgetDocument(html, {}), srcdocError: null };
    } catch (e) {
      return { srcdoc: null, srcdocError: e?.message || String(e) };
    }
  }, [html, streaming]);

  useEffect(() => {
    if (srcdocError) setErrorMsg(srcdocError);
  }, [srcdocError]);

  // Reset state when widget content changes so stale error/ready/height don't carry over.
  useEffect(() => {
    setReady(false);
    setHeight(MIN_HEIGHT);
    setErrorMsg(null);
  }, [html]);

  // Ready-timeout: if no widget:ready in 5s, surface a fallback.
  useEffect(() => {
    if (streaming || ready || errorMsg) return undefined;
    const t = setTimeout(() => {
      if (!iframeRef.current) return;
      setErrorMsg("Widget failed to start (no ready event within 5s).");
    }, READY_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [streaming, ready, errorMsg, srcdoc]);

  // postMessage listener
  useEffect(() => {
    if (streaming) return undefined;
    const onMessage = (ev) => {
      if (ev.source !== iframeRef.current?.contentWindow) return;
      const data = ev.data;
      if (!data || data.source !== "plex-widget") return;
      switch (data.type) {
        case "widget:ready":
          setReady(true);
          break;
        case "widget:height":
          if (typeof data.height === "number") {
            const h = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, data.height));
            setHeight(h);
          }
          break;
        case "widget:error":
          setErrorMsg(String(data.message || "Widget runtime error"));
          break;
        default:
          break;
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [streaming]);

  if (streaming) {
    return (
      <div className="widget-host widget-host--ghost" role="status" aria-live="polite">
        <div className="widget-host__ghost-header">
          <span aria-hidden="true">🧩</span>
          <span className="widget-host__ghost-title">{title || "Building widget…"}</span>
        </div>
        <div className="widget-host__ghost-sub">Building widget…</div>
        <div className="widget-host__skel widget-host__skel--1" />
        <div className="widget-host__skel widget-host__skel--2" />
        <div className="widget-host__skel widget-host__skel--3" />
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="widget-host widget-host--error" role="alert">
        <strong>Widget failed.</strong>
        <div className="widget-host__error-msg">{errorMsg}</div>
      </div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
      loading="lazy"
      srcDoc={srcdoc || ""}
      title={title || "Widget"}
      aria-label={`Interactive widget: ${title || "untitled"}`}
      style={{ width: "100%", height: `${height}px`, border: "none" }}
    />
  );
}
